import React, { useState, useEffect, useMemo } from 'react';
import {
  getQuotes, createQuote, updateQuote, deleteQuote,
  pushQuoteToXero, convertQuoteToWorkOrder, getUsers, getXeroStatus, fetchQuotePdfUrl,
  getEmailStatus, emailQuote, remindQuote, draftQuoteLineItemsWithAI, sendTestQuoteEmail,
  previewQuoteEmail,
} from '../../services/api';
import { PageHeader, StatusBadge } from '../../components/Shared';
import { SortBy, buildComparator } from '../../components/SortBy';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Plus, Pencil, Trash2, Upload, ArrowRightLeft, FileText, X, ExternalLink, CheckCircle2, Download, Mail, Send, Eye, BellRing, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { PdfPreviewDialog } from '../../components/PdfPreviewDialog';
import { usePdfPreview } from '../../hooks/usePdfPreview';
import { QUOTE_TONE_PRESETS } from './quoteEmailTonePresets';

const QUOTE_STATUS = {
  draft: { label: 'Draft', color: 'bg-slate-100 text-slate-800 border-slate-200' },
  sent: { label: 'Sent', color: 'bg-blue-100 text-blue-800 border-blue-200' },
  accepted: { label: 'Accepted', color: 'bg-green-100 text-green-800 border-green-200' },
  declined: { label: 'Declined', color: 'bg-red-100 text-red-800 border-red-200' },
  converted: { label: 'Converted', color: 'bg-indigo-100 text-indigo-800 border-indigo-200' },
  expired: { label: 'Expired', color: 'bg-amber-100 text-amber-800 border-amber-200' },
};

const emptyForm = {
  customerId: '', title: '', description: '',
  lineItems: [{ description: '', quantity: 1, unit_amount: 0 }],
  expiryDate: '', status: 'draft',
};

const Quotes = () => {
  const pdf = usePdfPreview();
  const [quotes, setQuotes] = useState([]);
  const [sort, setSort] = useState({ key: 'created', dir: 'desc' });
  // Apply the user's chosen sort to the server-returned list. We treat
  // string ids/numbers naturally so QT-2510 sorts after QT-2509.
  const sortedQuotes = useMemo(() => {
    const getter = (q, key) => {
      switch (key) {
        case 'customer': return (q.customer || '').toLowerCase();
        case 'amount': return Number(q.amount) || 0;
        case 'created': return q.createdAt ? new Date(q.createdAt).getTime() : 0;
        case 'expiry': return q.expiryDate ? new Date(q.expiryDate).getTime() : 0;
        case 'status': return (q.status || '').toLowerCase();
        case 'number':
        default: return String(q.xeroQuoteNumber || q.id || '');
      }
    };
    return [...quotes].sort(buildComparator(sort, getter));
  }, [quotes, sort]);
  const [customers, setCustomers] = useState([]);
  const [xeroConnected, setXeroConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [emailConfigured, setEmailConfigured] = useState(false);
  const [emailTarget, setEmailTarget] = useState(null); // {q, customerEmail}
  const [emailRecipient, setEmailRecipient] = useState('');
  const [emailNote, setEmailNote] = useState('');
  const [emailSending, setEmailSending] = useState(false);

  const refresh = () => getQuotes().then(setQuotes);

  useEffect(() => {
    Promise.all([getQuotes(), getUsers(), getXeroStatus(), getEmailStatus().catch(() => ({ configured: false }))])
      .then(([q, u, x, e]) => {
        setQuotes(q);
        setCustomers(u.filter(usr => usr.role === 'customer'));
        setXeroConnected(!!x?.connected);
        setEmailConfigured(!!e?.configured);
      })
      .finally(() => setLoading(false));
  }, []);

  const total = form.lineItems.reduce((a, li) => a + (Number(li.quantity) || 0) * (Number(li.unit_amount) || 0), 0);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setEditOpen(true); };

  const openPdfPreview = (q) => pdf.preview(() => fetchQuotePdfUrl(q.id));
  const downloadPdf = (q) => pdf.download(() => fetchQuotePdfUrl(q.id));

  const openEmail = (q) => {
    const cust = customers.find(c => c.id === q.customerId);
    setEmailTarget({ q, customerEmail: cust?.email || '' });
    setEmailRecipient(cust?.email || '');
    setEmailNote('');
    // Reset all editable overrides so the next preview starts clean.
    setEditedSubject('');
    setEditedGreetingLine('');
    setEditedBodyText('');
    setEditedButtonLabel('');
    setEditedCloser('');
    setPreviewData(null);
    setPreviewPdfUrl(null);
    setPreviewPdfName('');
  };

  const sendEmail = async () => {
    if (!emailTarget) return;
    setEmailSending(true);
    try {
      // Ship whatever the admin has typed in the preview editors. The
      // backend treats empty strings as "use the default template" so
      // there's no need for a client-side filter that might silently
      // drop an edit whose value happens to equal the default.
      const res = await emailQuote(emailTarget.q.id, {
        to: emailRecipient.trim() || undefined,
        note: emailNote.trim(),
        subject: editedSubject || '',
        greeting_line: editedGreetingLine || '',
        body_text: editedBodyText || '',
        button_label: editedButtonLabel || '',
        closer: editedCloser || '',
      });
      toast.success(`Quote sent to ${res.to}${res.pdfSource === 'xero' ? ' (Xero PDF)' : ''}`);
      setEmailTarget(null);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Email failed');
    } finally {
      setEmailSending(false);
    }
  };

  const sendTestEmail = async () => {
    if (!emailTarget) return;
    setEmailSending(true);
    try {
      const res = await sendTestQuoteEmail(emailTarget.q.id, {
        note: emailNote.trim(),
        subject: editedSubject || '',
        greeting_line: editedGreetingLine || '',
        body_text: editedBodyText || '',
        button_label: editedButtonLabel || '',
        closer: editedCloser || '',
      });
      toast.success(`Test copy sent to your inbox (${res.to}). The quote is unchanged.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test send failed');
    } finally {
      setEmailSending(false);
    }
  };

  // ---------- Rich preview state ----------
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);
  const [previewPdfName, setPreviewPdfName] = useState('');
  const [editingBody, setEditingBody] = useState(false);
  const [editedSubject, setEditedSubject] = useState('');
  const [editedGreetingLine, setEditedGreetingLine] = useState('');
  const [editedBodyText, setEditedBodyText] = useState('');
  const [editedButtonLabel, setEditedButtonLabel] = useState('');
  const [editedCloser, setEditedCloser] = useState('');

  const openPreview = async () => {
    if (!emailTarget) return;
    setPreviewLoading(true);
    setPreviewOpen(true);
    setEditingBody(false);
    if (previewPdfUrl) {
      try { URL.revokeObjectURL(previewPdfUrl); } catch { /* noop */ }
    }
    setPreviewPdfUrl(null);
    setPreviewPdfName('');
    try {
      const [body, pdf] = await Promise.all([
        previewQuoteEmail(emailTarget.q.id, { note: emailNote.trim() }),
        fetchQuotePdfUrl(emailTarget.q.id).catch(() => null),
      ]);
      setPreviewData(body);
      setEditedSubject(body.subject || body.defaultSubject || '');
      setEditedGreetingLine(body.defaultGreeting || '');
      setEditedBodyText(body.defaultBodyText || '');
      setEditedButtonLabel(body.defaultButtonLabel || '');
      setEditedCloser(body.defaultCloser || '');
      if (pdf) {
        setPreviewPdfUrl(pdf.url);
        setPreviewPdfName(pdf.filename || `${emailTarget.q.id}.pdf`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load preview');
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewOpen(false);
    if (previewPdfUrl) {
      try { URL.revokeObjectURL(previewPdfUrl); } catch { /* noop */ }
    }
    setPreviewPdfUrl(null);
    setPreviewPdfName('');
    setEditingBody(false);
  };

  // Live re-render whenever the admin edits any field in the preview
  // dialog. Debounced 500 ms so we don't hammer the backend on every
  // keystroke. Same seam as the invoice email preview.
  useEffect(() => {
    if (!previewOpen || !previewData || !editingBody || !emailTarget) return;
    const handle = setTimeout(() => {
      previewQuoteEmail(emailTarget.q.id, {
        note: emailNote.trim(),
        subject: editedSubject || '',
        greeting_line: editedGreetingLine || '',
        body_text: editedBodyText || '',
        button_label: editedButtonLabel || '',
        closer: editedCloser || '',
      }).then((body) => {
        setPreviewData((prev) => prev ? { ...prev, html: body.html, subject: body.subject } : body);
      }).catch(() => { /* silent — keep old preview */ });
    }, 500);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editedSubject, editedGreetingLine, editedBodyText, editedButtonLabel, editedCloser, editingBody]);
  const openEdit = (q) => {
    setEditing(q);
    setForm({
      customerId: q.customerId,
      title: q.title,
      description: q.description || '',
      lineItems: (q.lineItems && q.lineItems.length) ? q.lineItems : [{ description: '', quantity: 1, unit_amount: 0 }],
      expiryDate: q.expiryDate || '',
      status: q.status || 'draft',
    });
    setEditOpen(true);
  };

  const addLine = () => setForm({ ...form, lineItems: [...form.lineItems, { description: '', quantity: 1, unit_amount: 0 }] });
  const removeLine = (i) => setForm({ ...form, lineItems: form.lineItems.filter((_, idx) => idx !== i) });
  const updateLine = (i, field, value) => {
    const copy = [...form.lineItems];
    copy[i] = { ...copy[i], [field]: value };
    setForm({ ...form, lineItems: copy });
  };

  const save = async () => {
    if (!form.title.trim() || !form.customerId) return toast.error('Title and customer are required');
    try {
      const payload = {
        ...form,
        lineItems: form.lineItems
          .filter(li => li.description && Number(li.quantity) > 0)
          .map(li => ({ description: li.description, quantity: Number(li.quantity), unit_amount: Number(li.unit_amount) })),
      };
      if (editing) await updateQuote(editing.id, payload);
      else await createQuote(payload);
      toast.success(editing ? 'Quote updated' : 'Quote created');
      setEditOpen(false);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };

  const remove = async (q) => {
    if (!window.confirm(`Delete ${q.id}?`)) return;
    try { await deleteQuote(q.id); toast.success('Deleted'); await refresh(); }
    catch { toast.error('Delete failed'); }
  };

  const pushXero = async (q) => {
    try {
      const res = await pushQuoteToXero(q.id);
      toast.success(`Pushed to Xero (${res.xeroStatus})`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Xero push failed');
    }
  };

  const convert = async (q) => {
    if (!window.confirm(`Convert ${q.id} to a Work Order + Invoice?`)) return;
    try {
      const res = await convertQuoteToWorkOrder(q.id);
      toast.success(`Created ${res.workOrderId} and ${res.invoiceId}`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Convert failed');
    }
  };

  // Reminder is eligible when a quote has been *sent* to the customer and
  // more than 3 days have passed since the last touch (initial send OR
  // last reminder), and no decision has been made yet.
  const reminderEligible = (q) => {
    if ((q.status || '') !== 'sent') return false;
    const last = q.lastReminderSentAt || q.sentAt || q.lastEmailedAt;
    if (!last) return true; // sent long ago, no timestamp — allow
    const days = (Date.now() - new Date(last).getTime()) / (1000 * 60 * 60 * 24);
    return days >= 3;
  };

  const sendReminder = async (q) => {
    if (!window.confirm(`Send a gentle reminder for ${q.id}?`)) return;
    try {
      const res = await remindQuote(q.id);
      toast.success(`Reminder sent to ${res.to}`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reminder failed');
    }
  };

  const [aiDrafting, setAiDrafting] = useState(false);
  const draftWithAI = async () => {
    if (!editing) {
      toast.error('Save the quote first, then re-open it to use AI drafting.');
      return;
    }
    setAiDrafting(true);
    try {
      const res = await draftQuoteLineItemsWithAI(editing.id);
      setForm({ ...form, lineItems: res.lineItems });
      toast.success(res.rationale ? `AI: ${res.rationale}` : 'Line items drafted — review the prices before saving.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI drafting failed');
    } finally {
      setAiDrafting(false);
    }
  };

  if (loading) return <div className="p-8 text-slate-500">Loading quotes...</div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <PageHeader
        title="Quotes"
        description="Draft, send and convert customer quotations. Two-way synced with Xero when connected."
        actions={
          <Button onClick={openCreate} className="bg-slate-900 hover:bg-slate-800" data-testid="new-quote-btn">
            <Plus className="h-4 w-4 mr-2" /> New Quote
          </Button>
        }
      />

      {!xeroConnected && (
        <div className="bg-amber-50 border border-amber-200 rounded-md px-4 py-3 text-sm text-amber-900">
          Xero not connected — quotes are saved locally. <a href="/admin/xero" className="font-medium underline">Connect Xero</a> to enable push & sync.
        </div>
      )}

      <SortBy
        storageKey="sort.quotes"
        defaultValue="created:desc"
        onChange={setSort}
        testId="quotes-sort"
        options={[
          { value: 'number:desc', label: 'Quote number — newest first' },
          { value: 'number:asc',  label: 'Quote number — oldest first' },
          { value: 'customer:asc',  label: 'Customer (A–Z)' },
          { value: 'customer:desc', label: 'Customer (Z–A)' },
          { value: 'amount:desc', label: 'Amount — high to low' },
          { value: 'amount:asc',  label: 'Amount — low to high' },
          { value: 'created:desc', label: 'Created date — newest' },
          { value: 'created:asc',  label: 'Created date — oldest' },
          { value: 'expiry:asc',  label: 'Expiry — soonest' },
          { value: 'expiry:desc', label: 'Expiry — latest' },
          { value: 'status:asc', label: 'Status (A–Z)' },
        ]}
      />

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left">
              <th className="px-4 py-3 font-medium text-slate-600 hidden sm:table-cell">ID</th>
              <th className="px-4 py-3 font-medium text-slate-600">Title</th>
              <th className="px-4 py-3 font-medium text-slate-600 hidden md:table-cell">Customer</th>
              <th className="px-4 py-3 font-medium text-slate-600 text-right md:text-left">Amount</th>
              <th className="px-4 py-3 font-medium text-slate-600 hidden sm:table-cell">Status</th>
              <th className="px-4 py-3 font-medium text-slate-600 hidden lg:table-cell">Xero</th>
              <th className="px-4 py-3 font-medium text-slate-600 hidden lg:table-cell">Expires</th>
              <th className="px-4 py-3 font-medium text-slate-600 text-right hidden sm:table-cell">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {sortedQuotes.map(q => {
              const cfg = QUOTE_STATUS[q.status] || QUOTE_STATUS.draft;
              return (
                <tr
                  key={q.id}
                  data-testid={`quote-row-${q.id}`}
                  onClick={() => openEdit(q)}
                  className="hover:bg-slate-50 cursor-pointer"
                  title="Click to edit — use the eye icon for a PDF preview"
                >
                  <td className="px-4 py-3 font-mono text-xs text-slate-600 hidden sm:table-cell">{q.id}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{q.title}</div>
                    {q.description && <div className="text-xs text-slate-500 truncate max-w-xs">{q.description}</div>}
                    <div className="md:hidden text-xs text-slate-500 mt-0.5">{q.customer}</div>
                    <div className="sm:hidden mt-1 flex items-center justify-between gap-2">
                      <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-medium border rounded ${cfg.color}`}>{cfg.label}</span>
                      <span
                        className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-600 hover:text-slate-900 underline underline-offset-2"
                        data-testid={`edit-quote-mobile-${q.id}`}
                      >
                        <Pencil className="h-3 w-3" /> Edit
                      </span>
                    </div>
                    {/* Mobile action bar — desktop uses the Actions column instead */}
                    <div
                      className="sm:hidden mt-2 flex items-center gap-2 flex-wrap"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        type="button"
                        onClick={() => openPdfPreview(q)}
                        disabled={pdf.busy}
                        className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded px-2 py-1"
                        data-testid={`preview-quote-mobile-${q.id}`}
                      >
                        <Eye className="h-3.5 w-3.5" /> PDF
                      </button>
                      <button
                        type="button"
                        onClick={() => openEmail(q)}
                        disabled={!emailConfigured || q.status === 'converted'}
                        className="inline-flex items-center gap-1 text-[11px] font-medium text-blue-800 bg-blue-50 hover:bg-blue-100 rounded px-2 py-1 disabled:opacity-40"
                        data-testid={`email-quote-mobile-${q.id}`}
                      >
                        <Mail className="h-3.5 w-3.5" /> Email
                      </button>
                      {xeroConnected && q.status !== 'converted' && (
                        <button
                          type="button"
                          onClick={() => pushXero(q)}
                          className="inline-flex items-center gap-1 text-[11px] font-medium text-indigo-800 bg-indigo-50 hover:bg-indigo-100 rounded px-2 py-1"
                          data-testid={`push-xero-mobile-${q.id}`}
                        >
                          <Upload className="h-3.5 w-3.5" /> Xero
                        </button>
                      )}
                      {q.status !== 'converted' && (
                        <button
                          type="button"
                          onClick={() => convert(q)}
                          className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-800 bg-emerald-50 hover:bg-emerald-100 rounded px-2 py-1"
                          data-testid={`convert-mobile-${q.id}`}
                        >
                          <ArrowRightLeft className="h-3.5 w-3.5" /> Convert
                        </button>
                      )}
                      {reminderEligible(q) && (
                        <button
                          type="button"
                          onClick={() => sendReminder(q)}
                          className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-800 bg-amber-50 hover:bg-amber-100 rounded px-2 py-1"
                          data-testid={`remind-mobile-${q.id}`}
                          title={`No decision after ${q.lastReminderSentAt || q.sentAt || q.lastEmailedAt ? '3+ days' : 'send'} — send a gentle nudge`}
                        >
                          <BellRing className="h-3.5 w-3.5" /> Remind
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => remove(q)}
                        className="inline-flex items-center gap-1 text-[11px] font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded px-2 py-1 ml-auto"
                        data-testid={`delete-quote-mobile-${q.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-700 hidden md:table-cell">{q.customer}</td>
                  <td className="px-4 py-3 text-slate-900 font-semibold text-right md:text-left">£{(q.amount || 0).toFixed(2)}</td>
                  <td className="px-4 py-3 hidden sm:table-cell">
                    <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium border rounded ${cfg.color}`}>{cfg.label}</span>
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    {q.xeroQuoteId
                      ? (
                        <a
                          href={`https://go.xero.com/Accounts/Receivable/Quotes/Edit/${q.xeroQuoteId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`xero-link-${q.id}`}
                          className="inline-flex items-center gap-1 text-xs text-blue-800 font-medium hover:text-blue-600 hover:underline transition-colors"
                          title="Open this quote in Xero"
                        >
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          <span>Synced · {q.xeroStatus || '...'}</span>
                          <ExternalLink className="h-3 w-3 opacity-60" />
                        </a>
                      )
                      : <span className="text-xs text-slate-400">Not synced</span>}
                  </td>
                  <td className="px-4 py-3 text-slate-600 hidden lg:table-cell">{q.expiryDate}</td>
                  <td className="px-4 py-3 text-right hidden sm:table-cell" onClick={(e) => e.stopPropagation()}>
                    <div className="inline-flex items-center gap-1">
                      <Button size="sm" variant="ghost" onClick={() => openPdfPreview(q)} disabled={pdf.busy} title="Preview PDF" data-testid={`preview-quote-${q.id}`}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => downloadPdf(q)} title="Download PDF" data-testid={`download-quote-${q.id}`}>
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openEmail(q)}
                        disabled={!emailConfigured || q.status === 'converted'}
                        title={
                          !emailConfigured ? 'Email not configured (set RESEND_API_KEY)' :
                          q.status === 'converted' ? 'Already converted' :
                          'Email this quote to the customer'
                        }
                        data-testid={`email-quote-${q.id}`}
                      >
                        <Mail className="h-4 w-4" />
                      </Button>
                      {xeroConnected && q.status !== 'converted' && (
                        <Button size="sm" variant="ghost" onClick={() => pushXero(q)} title="Push to Xero" data-testid={`push-xero-${q.id}`}>
                          <Upload className="h-4 w-4" />
                        </Button>
                      )}
                      {q.status !== 'converted' && (
                        <Button size="sm" variant="ghost" onClick={() => convert(q)} title="Convert to Work Order + Invoice" data-testid={`convert-${q.id}`}>
                          <ArrowRightLeft className="h-4 w-4" />
                        </Button>
                      )}
                      {reminderEligible(q) && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => sendReminder(q)}
                          title="Send a gentle reminder — no decision in 3+ days"
                          data-testid={`remind-quote-${q.id}`}
                        >
                          <BellRing className="h-4 w-4 text-amber-600" />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => openEdit(q)} data-testid={`edit-quote-${q.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(q)} data-testid={`delete-quote-${q.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {quotes.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-500">
                <FileText className="h-10 w-10 mx-auto text-slate-300 mb-2" />
                No quotes yet. Create your first quote to get started.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Create / edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${editing.id}` : 'Create Quote'}</DialogTitle>
            <DialogDescription>Line items calculate the total automatically. All prices exclude VAT.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Customer</Label>
                <Select value={form.customerId} onValueChange={v => setForm({ ...form, customerId: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="quote-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
                  <SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.company || c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Expiry date</Label>
                <Input type="date" className="mt-1.5" value={form.expiryDate} onChange={e => setForm({ ...form, expiryDate: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Title</Label>
              <Input className="mt-1.5" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g. Annual PAT testing quote" data-testid="quote-title-input" />
            </div>
            <div>
              <Label>Description / scope</Label>
              <Textarea className="mt-1.5" rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Brief description of the work" />
            </div>

            {editing && (
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="sent">Sent to customer</SelectItem>
                    <SelectItem value="accepted">Accepted</SelectItem>
                    <SelectItem value="declined">Declined</SelectItem>
                    <SelectItem value="expired">Expired</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                <Label>Line items</Label>
                <div className="flex items-center gap-2">
                  {editing && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={draftWithAI}
                      disabled={aiDrafting}
                      className="border-violet-200 text-violet-700 hover:bg-violet-50 hover:text-violet-800"
                      data-testid="ai-draft-lines-btn"
                      title={editing?.serviceRequestId
                        ? `Draft priced line items from ${editing.serviceRequestId}`
                        : 'Draft priced line items from the quote title & description'}
                    >
                      <Sparkles className="h-3.5 w-3.5 mr-1" />
                      {aiDrafting ? 'Drafting…' : 'Draft with AI'}
                    </Button>
                  )}
                  <Button type="button" size="sm" variant="outline" onClick={addLine} data-testid="add-line-btn">
                    <Plus className="h-3.5 w-3.5 mr-1" /> Add line
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                {form.lineItems.map((li, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-6">
                      <Input placeholder="Description" value={li.description} onChange={e => updateLine(i, 'description', e.target.value)} />
                    </div>
                    <div className="col-span-2">
                      <Input type="number" step="0.01" placeholder="Qty" value={li.quantity} onChange={e => updateLine(i, 'quantity', e.target.value)} />
                    </div>
                    <div className="col-span-3">
                      <Input type="number" step="0.01" placeholder="Unit price" value={li.unit_amount} onChange={e => updateLine(i, 'unit_amount', e.target.value)} />
                    </div>
                    <div className="col-span-1 flex justify-end">
                      <Button type="button" size="icon" variant="ghost" onClick={() => removeLine(i)} disabled={form.lineItems.length === 1}>
                        <X className="h-4 w-4 text-slate-500" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="text-right text-sm text-slate-600 mt-2">
                Total: <span className="font-bold text-slate-900 text-base">£{total.toFixed(2)}</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-slate-900 hover:bg-slate-800" data-testid="save-quote-btn">
              {editing ? 'Save changes' : 'Create quote'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email quote to customer */}
      <Dialog open={!!emailTarget} onOpenChange={(o) => !o && setEmailTarget(null)}>
        <DialogContent
          data-testid="email-quote-dialog"
          className="max-w-lg w-[calc(100vw-1rem)] max-h-[90vh] overflow-y-auto overflow-x-hidden"
        >
          <DialogHeader>
            <DialogTitle>Email quote {emailTarget?.q?.id} to customer</DialogTitle>
            <DialogDescription>
              Sends the {emailTarget?.q?.xeroQuoteId ? 'official Xero-branded' : 'Lutrom-branded'} PDF as an attachment, plus a one-click link for the customer to accept or decline online. Admin email will be CC'd by default.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Recipient email</Label>
              <Input
                type="email"
                className="mt-1.5"
                value={emailRecipient}
                onChange={(e) => setEmailRecipient(e.target.value)}
                placeholder={emailTarget?.customerEmail || 'customer@example.com'}
                data-testid="email-quote-recipient"
              />
              {emailTarget?.customerEmail && emailRecipient === emailTarget.customerEmail && (
                <p className="text-xs text-slate-500 mt-1">Customer's saved email</p>
              )}
            </div>
            <div>
              <Label>Optional note</Label>
              <textarea
                rows={3}
                className="mt-1.5 w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-200"
                value={emailNote}
                onChange={(e) => setEmailNote(e.target.value)}
                placeholder="e.g. As discussed on the phone — happy to walk through any item"
                data-testid="email-quote-note"
              />
            </div>
            {emailTarget?.q?.status === 'draft' && (
              <p className="text-xs bg-blue-50 border border-blue-200 text-blue-800 rounded px-3 py-2">
                This quote is currently a draft. Sending will mark it as <strong>sent</strong> so the customer can accept or decline.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailTarget(null)}>Cancel</Button>
            <Button
              type="button"
              variant="outline"
              onClick={openPreview}
              disabled={emailSending}
              className="border-slate-300 text-slate-800 hover:bg-slate-50"
              data-testid="email-quote-preview-btn"
            >
              <Eye className="h-4 w-4 mr-2" />
              Preview email & PDF
            </Button>
            <Button
              variant="outline"
              onClick={sendTestEmail}
              disabled={emailSending}
              className="border-violet-200 text-violet-700 hover:bg-violet-50 hover:text-violet-800"
              data-testid="email-quote-test-btn"
              title="Send a preview copy to your own inbox — no changes to the quote"
            >
              <Send className="h-4 w-4 mr-2" />
              Send test to me
            </Button>
            <Button
              onClick={sendEmail}
              disabled={emailSending || !emailRecipient.trim()}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="email-quote-send-btn"
            >
              <Send className="h-4 w-4 mr-2" />
              {emailSending ? 'Sending...' : 'Send quote'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PdfPreviewDialog
        url={pdf.url}
        filename={pdf.filename}
        onClose={pdf.close}
        title="Quote preview"
      />

      {/* Rich preview dialog — mirrors the invoice email preview.
          Shows resolved headers, the rendered email HTML (with an
          "Edit body" toggle exposing subject / greeting / body /
          button label / sign-off), and the PDF that will be attached.
          The primary "Send quote" action mirrors the email dialog's
          send button so admins can confirm-and-send without hopping
          back. */}
      <Dialog open={previewOpen} onOpenChange={(v) => { if (!v) closePreview(); }}>
        <DialogContent
          data-testid="email-quote-preview-dialog"
          className="max-w-6xl w-[calc(100vw-1rem)] max-h-[92vh] overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle>Email preview · {previewData?.quoteId || emailTarget?.q?.id}</DialogTitle>
            <DialogDescription>
              This is exactly what the customer will receive when you tap Send. Edit the copy or attached PDF first if anything looks wrong.
            </DialogDescription>
          </DialogHeader>
          {previewLoading || !previewData ? (
            <div className="py-12 text-center text-slate-500 text-sm" data-testid="email-quote-preview-loading">
              Loading preview…
            </div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs space-y-1.5" data-testid="email-quote-preview-headers">
                <div className="flex gap-2"><span className="w-16 text-slate-500 uppercase tracking-wider">From</span><span className="text-slate-900 font-medium">{previewData.sender}</span></div>
                <div className="flex gap-2"><span className="w-16 text-slate-500 uppercase tracking-wider">To</span><span className="text-slate-900 font-medium">{emailRecipient || <em className="text-slate-400">(none yet)</em>}</span></div>
                {previewData.adminCc && (
                  <div className="flex gap-2"><span className="w-16 text-slate-500 uppercase tracking-wider">Cc</span><span className="text-slate-900">{previewData.adminCc}</span></div>
                )}
                <div className="flex gap-2"><span className="w-16 text-slate-500 uppercase tracking-wider">Subject</span><span className="text-slate-900 font-semibold">{previewData.subject}</span></div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Email body</div>
                    <button
                      type="button"
                      onClick={() => setEditingBody((v) => !v)}
                      className="text-[11px] text-emerald-700 hover:text-emerald-900 underline underline-offset-2"
                      data-testid="email-quote-preview-edit-toggle"
                    >
                      {editingBody ? '← Back to preview' : 'Edit body'}
                    </button>
                  </div>
                  {editingBody ? (
                    <>
                      {/* Tone presets — one tap rewrites subject +
                          greeting + body + button + closer in the
                          chosen voice. The admin can still tweak
                          individual fields afterwards; a preset is a
                          starting point, not a lock. */}
                      <div className="flex items-center gap-1.5 flex-wrap" data-testid="email-quote-preview-tone-presets">
                        <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mr-1">
                          Tone
                        </span>
                        {QUOTE_TONE_PRESETS.map(({ id, label, build }) => (
                          <button
                            key={id}
                            type="button"
                            onClick={() => {
                              const preset = build(previewData);
                              setEditedSubject(preset.subject);
                              setEditedGreetingLine(preset.greeting_line);
                              setEditedBodyText(preset.body_text);
                              setEditedButtonLabel(preset.button_label);
                              setEditedCloser(preset.closer);
                            }}
                            className="text-[11px] font-medium text-emerald-800 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-full px-2.5 py-0.5"
                            data-testid={`email-quote-preview-tone-${id}`}
                            title={`Rewrite every field in a ${label.toLowerCase()} tone`}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                      <div className="space-y-2">
                        <div>
                          <Label className="text-[10px] uppercase tracking-wider text-slate-500">Subject line</Label>
                          <Input
                            value={editedSubject}
                            onChange={(e) => setEditedSubject(e.target.value)}
                            className="mt-1 h-8 text-sm"
                            data-testid="email-quote-preview-subject-editor"
                          />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase tracking-wider text-slate-500">
                            Greeting <span className="text-slate-400 normal-case">(the "Hi X," line)</span>
                          </Label>
                          <Input
                            value={editedGreetingLine}
                            onChange={(e) => setEditedGreetingLine(e.target.value)}
                            className="mt-1 h-8 text-sm"
                            data-testid="email-quote-preview-greeting-editor"
                          />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase tracking-wider text-slate-500">Body copy</Label>
                          <Textarea
                            value={editedBodyText}
                            onChange={(e) => setEditedBodyText(e.target.value)}
                            className="w-full h-[34vh] text-sm leading-relaxed mt-1"
                            placeholder="Blank line = new paragraph."
                            data-testid="email-quote-preview-body-editor"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Accept button</Label>
                            <Input
                              value={editedButtonLabel}
                              onChange={(e) => setEditedButtonLabel(e.target.value)}
                              className="mt-1 h-8 text-sm"
                              data-testid="email-quote-preview-button-editor"
                            />
                          </div>
                          <div>
                            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Sign-off</Label>
                            <Input
                              value={editedCloser}
                              onChange={(e) => setEditedCloser(e.target.value)}
                              className="mt-1 h-8 text-sm"
                              data-testid="email-quote-preview-closer-editor"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="text-[10px] text-slate-500 flex items-center justify-between mt-1">
                        <span>Every visible line is editable — Lutrom logo + PDF attachment stay in place.</span>
                        <button
                          type="button"
                          onClick={() => {
                            setEditedSubject(previewData.defaultSubject || '');
                            setEditedGreetingLine(previewData.defaultGreeting || '');
                            setEditedBodyText(previewData.defaultBodyText || '');
                            setEditedButtonLabel(previewData.defaultButtonLabel || '');
                            setEditedCloser(previewData.defaultCloser || '');
                          }}
                          className="text-emerald-700 hover:text-emerald-900 underline underline-offset-2"
                          data-testid="email-quote-preview-body-reset"
                        >
                          Reset to template
                        </button>
                      </div>
                    </>
                  ) : (
                    <iframe
                      srcDoc={previewData.html}
                      title="Quote email preview"
                      className="w-full h-[60vh] border border-slate-200 rounded-md bg-white"
                      sandbox=""
                      data-testid="email-quote-preview-body-iframe"
                    />
                  )}
                </div>

                <div className="space-y-1.5">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center justify-between">
                    <span>Attached PDF · {previewPdfName || `${emailTarget?.q?.id}.pdf`}</span>
                    {previewPdfUrl && (
                      <a
                        href={previewPdfUrl}
                        download={previewPdfName || `${emailTarget?.q?.id}.pdf`}
                        className="text-[11px] text-emerald-700 hover:text-emerald-900 underline underline-offset-2 normal-case tracking-normal"
                        data-testid="email-quote-preview-pdf-download"
                      >
                        Download
                      </a>
                    )}
                  </div>
                  {previewPdfUrl ? (
                    <iframe
                      src={previewPdfUrl}
                      title="Quote PDF preview"
                      className="w-full h-[60vh] border border-slate-200 rounded-md bg-white"
                      data-testid="email-quote-preview-pdf-iframe"
                    />
                  ) : (
                    <div className="w-full h-[60vh] border border-dashed border-slate-300 rounded-md flex items-center justify-center text-xs text-slate-500">
                      Could not render PDF preview.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closePreview}>Close preview</Button>
            <Button
              variant="outline"
              onClick={sendTestEmail}
              disabled={emailSending || previewLoading}
              className="border-violet-200 text-violet-700 hover:bg-violet-50 hover:text-violet-800"
              data-testid="email-quote-preview-test-btn"
            >
              <Send className="h-4 w-4 mr-2" />
              Send test to me
            </Button>
            <Button
              onClick={async () => { await sendEmail(); closePreview(); }}
              disabled={emailSending || !emailRecipient.trim() || previewLoading}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="email-quote-preview-send-btn"
            >
              <Send className="h-4 w-4 mr-2" />
              {emailSending ? 'Sending...' : 'Send quote'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default Quotes;
